/**
 * Wall-related collision checks for placed objects.
 * Delegates to wallFixtureCollision.js for wall-mounted fixtures.
 * Adds parent wall bounds check for floor-level fixtures.
 */

const {
  checkWallFixtureOverlap,
  projectOntoWallAxis,
  getOBBCorners,
} = require('../vanity-assembly/wallFixtureCollision');

/**
 * Check if a wall-mounted fixture collides with anything on/near its parent wall.
 * Wraps checkWallFixtureOverlap with the unified collision output format.
 *
 * @param {Object} object - Wall-mounted fixture
 * @param {Object} roomData - { walls, objects, doors, windows, openings }
 * @param {Object} parentWall - The wall the fixture is mounted on
 * @returns {Array<{type, target, reason}>}
 */
function checkWallMountedCollisions(object, roomData, parentWall) {
  const result = checkWallFixtureOverlap(
    object.center,
    object.dimensions,
    roomData,
    parentWall,
    0.0508, // 2" default margin
    object.id,
    false,
    object.parentWallIds,
    object
  );

  if (result.collides) {
    return [
      {
        type:
          result.collidingElement?.type === 'ceiling'
            ? 'ceiling'
            : result.collidingElement?.type === 'floor'
              ? 'floor'
              : 'parent-wall-bounds',
        target: result.collidingElement || { id: 'unknown', type: 'unknown' },
        reason: result.reason,
      },
    ];
  }

  return [];
}

/**
 * Check if a floor-level fixture extends beyond its parent wall's endpoints.
 * Uses OBB projection onto wall axis.
 *
 * @param {Object} object - Floor-level fixture
 * @param {Object[]} walls - All room walls
 * @param {Object} [attachedWall] - The wall to check bounds against (overrides parentWallIds lookup)
 * @returns {Array<{type, target, reason}>}
 */
function checkParentWallBounds(object, walls, attachedWall) {
  const collisions = [];

  // Corner tubs span two walls — they inherently extend beyond a single
  // parent wall's endpoints, so wall-bounds checking doesn't apply.
  const objType = (object.type || '').toLowerCase();
  if (
    (objType === 'bathtub' || objType === 'tub-support-box') &&
    object['tub-surround-polygon']?.points?.length >= 5
  ) {
    return collisions;
  }

  const parentWall =
    attachedWall ||
    (object.parentWallIds?.length
      ? walls.find((w) => w.id === object.parentWallIds[0])
      : null);
  if (!parentWall) return collisions;

  const proj = projectOntoWallAxis(object.center, parentWall);
  if (!proj) return collisions;

  const { t, wallLength } = proj;
  const start = parentWall.alignment?.bottomLeft;
  const end = parentWall.alignment?.bottomRight;
  if (!start || !end) return collisions;

  const wallDir = {
    x: (end.x - start.x) / wallLength,
    z: (end.z - start.z) / wallLength,
  };

  // Project OBB corners onto wall axis
  const corners = getOBBCorners(object, 0);
  let minProj = Infinity;
  let maxProj = -Infinity;
  for (const c of corners) {
    const p = (c.x - start.x) * wallDir.x + (c.z - start.z) * wallDir.z;
    if (p < minProj) minProj = p;
    if (p > maxProj) maxProj = p;
  }

  const eps = 0.005; // 5mm tolerance
  if (minProj < -eps || maxProj > wallLength + eps) {
    const overshoot = Math.max(-minProj, maxProj - wallLength);
    collisions.push({
      type: 'parent-wall-bounds',
      target: { id: parentWall.id, type: 'wall' },
      reason: `Extends ${(overshoot * 1000).toFixed(0)}mm beyond parent wall`,
    });
  }

  return collisions;
}

module.exports = {
  checkWallMountedCollisions,
  checkParentWallBounds,
};
